import {DeadlineListElementType, DeadlineListMapType, DeadlineSearchParamType} from "../interface";
import {Dispatch, SetStateAction} from "react";
import EmptyTableRow from "../../../../common/EmptyTableRow";
import {PaginationMain} from "../../../../../components/common/pagination/PaginationMain";
import {useModal} from "../../../../../hooks";
import {useUpdateDeadlineProgress} from "../hooks/useUpdateDeadlineProgress";
import useGoToNavigation from "../../../../../util/useGotoNavigation";
import {useIssueTax} from "../hooks/useIssueTax";
import {useIssueTaxIndividual} from "../hooks/useIssueTaxIndividual"; // 내부수정: 세금계산서 개별발행
import {useDownloadDeadlineFile} from "../hooks/useDownloadDeadlineFile";
import DisplayNumber from "../../../../../components/common/number/DisplayNumber";
import AuthorityChecker from "../../../../../components/common/authorityGuard/AuthorityChecker";

interface PropsType {
    searchParam: DeadlineSearchParamType,
    deadline: DeadlineListMapType,
    selectedItems: DeadlineListElementType[],
    setSelectedItems: Dispatch<SetStateAction<DeadlineListElementType[]>>
}

export default function List({searchParam, deadline, selectedItems, setSelectedItems}: PropsType) {
    const {handleModal} = useModal();
    const {goToNavigation} = useGoToNavigation();
    const {mutate: downloadDeadline} = useDownloadDeadlineFile();
    const deadlineList = deadline.list ?? []
    const deadlineTotalCount = deadline.totalCount ?? 0

    const isAllCheck = deadlineList.length !== 0 && deadlineList.every(el => selectedItems.includes(el))

    const closeCallback = () => setSelectedItems([])
    const {mutate: mutateUpdateDeadlineProgress} = useUpdateDeadlineProgress({closeCallback: closeCallback})
    const {mutate: mutateIssueTax} = useIssueTax({closeCallback: closeCallback})
    const {mutate: mutateIssueTaxIndividual} = useIssueTaxIndividual({closeCallback: closeCallback}) // 내부수정: 개별 발행

    const clickAllCheck = () => {
        if (isAllCheck) {
            const list = selectedItems.filter(el => !deadlineList.includes(el))
            setSelectedItems(list)
        } else {
            // @ts-ignore
            const list = [...new Set([...selectedItems, ...deadlineList])]
            setSelectedItems(list)
        }
    }

    const clickCheck = (el: DeadlineListElementType) => {
        if (selectedItems.includes(el)) {
            const list = selectedItems.filter(el2 => el.newShipmentId !== el2.newShipmentId)
            setSelectedItems(list)
        } else {
            setSelectedItems([...selectedItems, el])
        }
    }

    const clickRow = (el: DeadlineListElementType, e: React.MouseEvent) => {
        if (!el.newShipmentId) return;
        const url = `order/deadline/detail/${el.newShipmentId}`;
        // 휠 클릭
        if (e.button === 1) {
            goToNavigation(url, {}, false, { newTab: true });
            return;
        }
        // ctrl / cmd
        if (e.ctrlKey || e.metaKey) {
            goToNavigation(url, {}, false, { newTab: true });
            return;
        }
        // 일반 클릭
        if (e.button === 0) {
            goToNavigation(url, {}, false);
        }
    };

    const btnToggleFinish = (key: "Y" | "N") => {
        const list = selectedItems.map(el => ({...el, endFlag: key}))
        // 내부수정: 총 합계 표시
        const count = selectedItems.length
        const totalSum = selectedItems.reduce((acc, el) => acc + (el.totalAmount ?? 0), 0)

        handleModal({
            isOpen: true,
            contents: key === "Y"
                ? `총 ${count}건 ${totalSum.toLocaleString()}원\n마감 처리하시겠습니까?`
                : `총 ${count}건 ${totalSum.toLocaleString()}원\n마감 취소하시겠습니까?`,
            type: "confirm",
            callback: () => mutateUpdateDeadlineProgress(list)
        })
    }

    // 내부수정: 총 합계 표시, 명칭 변경
    const btnInvoiceCombined  = () => {
        //mutateIssueTax(selectedItems)
        const count = selectedItems.length
        const totalSum = selectedItems.reduce((acc, el) => acc + (el.totalAmount ?? 0), 0)

        handleModal({
            isOpen: true,
            contents: `총 ${count}건 ${totalSum.toLocaleString()}원\n세금계산서 합산 발행 요청하시겠습니까?`,
            type: "confirm",
            callback: () => mutateIssueTax(selectedItems)
        })
    }

    // 내부수정: 개별 발행요청 신규
    const btnInvoiceIndividual = () => {
        const count = selectedItems.length
        const totalSum = selectedItems.reduce((acc, el) => acc + (el.totalAmount ?? 0), 0)
        const normalCount = selectedItems.filter(el => el.endType === "일반").length
        const monthlyCount = selectedItems.filter(el => el.endType === "월마감").length
        const reverseCount = selectedItems.filter(el => el.endType === "역발행").length

        handleModal({
            isOpen: true,
            contents: `일반 ${normalCount}건, 월마감 ${monthlyCount}건, 역발행 ${reverseCount}건\n총 ${count}건 ${totalSum.toLocaleString()}원\n세금계산서 개별 발행 요청하시겠습니까?`,
            type: "confirm",
            callback: () => mutateIssueTaxIndividual(selectedItems)
        })
    }

    // 선택된 항목이 없거나, 선택된 항목 중에 상태가 Y(마감)인게 있으면 마감 버튼 비활성화
    const isBtnFinishDisabledY = selectedItems.length === 0 || selectedItems.some(el => el.endFlag === "Y")

    // 선택된 항목이 없거나, 선택된 항목 중에 상태가 N(미마감)인게 있으면 마감취소 버튼 비활성화
    const isBtnFinishDisabledN = selectedItems.length === 0 || selectedItems.some(el => el.endFlag === "N")

    // 선택된 항목이 없거나, 선택된 항목 중에 상태 N(미마감)인게 있거나 매출처가 다른 항목이 있으면 세금계산서 발행요청 버튼 비활성화
    const isBtnInvoiceCombinedDisabled = selectedItems.length === 0 
        || selectedItems.some(el => el.endFlag === "N") 
        || selectedItems.some(el => el.customerName !== selectedItems.at(0)?.customerName)
        || selectedItems.some(el => el.endType !== selectedItems.at(0)?.endType) // 내부수정: 마감구분 동일성 체크 추가

    // 내부수정: 개별 발행은 마감상태(Y)만 체크, 매출처/마감구분 무관
    const isBtnInvoiceIndividualDisabled = selectedItems.length === 0
        || selectedItems.some(el => el.endFlag === "N")


    return <div className="content_wrap">
        <div className="table_top">
            <h6 className="table_stats">마감 목록(<span>{deadlineTotalCount}</span>건)</h6>
            <div className="table_toolbar">
                <button className="btn btn-dark w-150px px-0" onClick={() => downloadDeadline(searchParam)}>
                    <img src={process.env.PUBLIC_URL + "/assets/images/icon_export.png"} alt=""/>엑셀 내보내기
                </button>
                <AuthorityChecker authorityNames={['taxWriteYn']}>
                    <button className="btn btn-primary ms-40px" onClick={btnInvoiceCombined}
                            disabled={isBtnInvoiceCombinedDisabled}>합산 발행요청
                    </button>
                    <button className="btn btn-primary ms-10px" onClick={btnInvoiceIndividual}
                            disabled={isBtnInvoiceIndividualDisabled}>개별 발행요청
                    </button>
                </AuthorityChecker>
                <AuthorityChecker authorityNames={['endWriteYn']}>
                    <button className="btn btn-info color2 w-80px p-0" onClick={() => btnToggleFinish("Y")}
                            disabled={isBtnFinishDisabledY}>마감
                    </button>
                    <button className="btn btn-outline-info w-80px p-0" onClick={() => btnToggleFinish("N")}
                            disabled={isBtnFinishDisabledN}>마감취소
                    </button>
                </AuthorityChecker>
            </div>
        </div>

        {/* 내부수정: table_scroll 삭제 */}
        <table className="table table-bordered my_table">
            <thead>
            <tr>
                <th style={{width: "3%"}}>
                    <div className="checkbox_outer">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            checked={isAllCheck}
                            onClick={clickAllCheck}
                        />
                    </div>
                </th>
                <th style={{width: "6%"}}>수주일자</th>
                {/* <th style={{width: "8%"}}>마감일자</th> */}
                <th style={{width: "5%"}}>마감구분</th>
                <th style={{width: "5%"}}>담당자</th>
                <th style={{width: "20%"}}>매출처</th>
                <th style={{width: "4%"}}>환종</th>
                <th style={{width: "5%"}}>환율</th>
                <th style={{width: "8%"}}>공급가액</th>
                <th style={{width: "7%"}}>부가세</th>
                <th style={{width: "8%"}}>금액</th>
                <th style={{width: "5%"}}>상태</th>
            </tr>
            </thead>
            {/* <tr className={`clickable ${selectedItems.map(el => el.newShipmentId).includes(el.newShipmentId) ? "lightblue" : ""}`}
                        onClick={() => clickRow(el)}></tr> */}
            <tbody>
            {deadlineList.length !== 0 ?
                deadlineList.map(el =>
                    <tr className={`clickable ${selectedItems.map(el => el.newShipmentId).includes(el.newShipmentId) ? "lightblue" : ""}`}
                        onMouseDown={(e) => clickRow(el, e)}>
                        <td className="text-center" onMouseDown={e => e.stopPropagation()}>
                            <div className="checkbox_outer">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={selectedItems.map(el => el.newShipmentId).includes(el.newShipmentId)}
                                    onClick={() => clickCheck(el)}/>
                            </div>
                        </td>
                        <td className="text-center">{el.newDtShipment}</td>
                        {/* <td className="text-center">{el.endDt}</td> */}
                        <td className="text-center">{el.endType}</td>
                        <td className="text-center">{el.owningUserName}</td>

                        <td className="text-center">{el.customerName}</td>
                        <td className="text-center">{el.currencyName}</td>
                        <td className="text-center"><DisplayNumber value={el.newDExchangeRate}/></td>
                        <td className="text-end"><DisplayNumber value={el.newDAmount}/></td>
                        <td className="text-end"><DisplayNumber value={el.taxTotal}/></td>
                        <td className="text-end"><DisplayNumber value={el.totalAmount}/></td>
                        <td className="text-center">{el.endState}</td>
                    </tr>
                )
                : <EmptyTableRow colSpan={12}/>}

                {/* 내부수정: 선택 합계 행 */}
                {selectedItems.length > 0 &&
                    <tr className="summary_row">
                        <td colSpan={7} className="text-end" style={{borderRight: "none"}}>
                            선택 합계 ({selectedItems.length}건)
                        </td>
                        <td className="text-end">
                            <DisplayNumber value={selectedItems.reduce((acc, el) => acc + (el.newDAmount ?? 0), 0)}/>
                        </td>
                        <td className="text-end">
                            <DisplayNumber value={selectedItems.reduce((acc, el) => acc + (el.taxTotal ?? 0), 0)}/>
                        </td>
                        <td className="text-end">
                            <DisplayNumber value={selectedItems.reduce((acc, el) => acc + (el.totalAmount ?? 0), 0)}/>
                        </td>
                        <td></td>
                    </tr>
                }
            </tbody>
        </table>
        

        <PaginationMain totalCount={deadlineTotalCount}/>
    </div>
}